import {
  Text,
  LinkBox,
  LinkOverlay,
  useTheme,
  VStack,
  Wrap,
  WrapItem,
  Grid,
  // Image,
} from '@chakra-ui/react'
import { useSpring } from 'react-spring'
import Image from 'next/image'
import NextLink from 'next/link'

import { getDateString } from '@/lib/utils/format/datetime'
import {
  DEFAULT_SPRING_CONFIG,
  AnimatedAspectRatio,
} from '@/lib/springComponent'

import { BlogListItem } from '@/features/blogs/types/blog'

import { BLOG as BLOG_ROUTE } from '@/routes'
import Tag from './Tag'

interface Props {
  data: BlogListItem
  id?: string
  direction?: 'row' | 'column'
  useOriginalImage?: boolean
}

function BlogItem({
  data,
  id,
  direction = 'column',
  useOriginalImage = false,
}: Props): React.ReactElement {
  const theme = useTheme()

  const [imageSpring, imageSpringApi] = useSpring(() => ({
    transform: 'perspective(1000px) scale(1) rotateX(0deg)',
    boxShadow: `0px 0px 0px 0px ${theme.colors.gray[400]}88`,
    config: DEFAULT_SPRING_CONFIG,
  }))

  return (
    <LinkBox as="article" id={id} sx={{ w: 'full' }}>
      <Grid
        templateColumns={direction === 'column' ? '1fr' : '11fr 9fr'}
        rowGap={4}
        columnGap={7}
        sx={{
          alignItems: 'stretch',
          transformOrigin: 'center top',
        }}
        onMouseEnter={() => {
          imageSpringApi.start({
            transform: 'perspective(1000px) scale(1.05) rotateX(12deg)',
            boxShadow: `0px 16px 10px -8px ${theme.colors.gray[400]}88`,
          })
        }}
        onMouseLeave={() => {
          imageSpringApi.start({
            transform: 'perspective(1000px) scale(1) rotateX(0deg)',
            boxShadow: `0px 0px 0px 0px ${theme.colors.gray[400]}88`,
          })
        }}
      >
        <AnimatedAspectRatio ratio={264 / 138} style={imageSpring}>
          <Image
            src={
              useOriginalImage || !data.image.thumbnail
                ? data.image.original
                : data.image.thumbnail
            }
            title={data.title}
            alt={data.title}
            placeholder="blur"
            blurDataURL={data.image.thumbnail ?? data.image.original}
            objectFit="cover"
            layout="fill"
          />
        </AnimatedAspectRatio>
        <VStack alignItems="left">
          <NextLink
            passHref
            href={{
              pathname: BLOG_ROUTE.pathname,
              query: {
                slug: data.slug,
              },
            }}
          >
            <LinkOverlay>
              <Text
                as="h2"
                sx={{
                  fontWeight: 'medium',
                  fontSize: {
                    base: 'md',
                    sm: direction === 'column' ? 'xl' : '2xl',
                  },
                }}
              >
                {data.title}
              </Text>
              {direction === 'row' && (
                <Text
                  sx={{
                    display: '-webkit-box',
                    '-webkit-line-clamp': '5',
                    '-webkit-box-orient': 'vertical',
                    overflow: 'hidden',
                    marginBottom: 4,
                  }}
                >
                  {data.meta_description}
                </Text>
              )}
            </LinkOverlay>
          </NextLink>
          <Text
            sx={{
              fontSize: {
                base: 'sm',
                sm: 'md',
              },
            }}
          >
            {getDateString(new Date(data.published_at))}
          </Text>
          <Wrap>
            {data.tags.map((tag, idx: number) => (
              <WrapItem key={idx}>
                <Tag {...tag} />
              </WrapItem>
            ))}
          </Wrap>
        </VStack>
      </Grid>
    </LinkBox>
  )
}

export default BlogItem
